import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { DailyAudio, DailyProvider } from '@daily-co/daily-react';

import api from '../api';
import { roomUrlFromPageUrl, pageUrlFromRoomUrl } from '../utils';

import  TutorUpcomingLessons  from '../../TutorUpcomingLessons';

import { post } from '../../../utils/api';
import { loadTutorEmails } from '../tutorEmails';


import HomeScreen from '../HomeScreen/HomeScreen';
import Call from '../Call/Call';
//import Header from '../Header/Header';
import Tray from '../Tray/Tray';
import HairCheck from '../HairCheck/HairCheck';

import { AuthContext } from '../../../contexts/AuthContext';
import { useAuth } from '../../../contexts/AuthContext';

/* We decide what UI to show to users based on the state of the app, which is dependent on the state of the call object. */
const STATE_IDLE = 'STATE_IDLE';
const STATE_CREATING = 'STATE_CREATING';
const STATE_JOINING = 'STATE_JOINING';
const STATE_JOINED = 'STATE_JOINED';
const STATE_LEAVING = 'STATE_LEAVING';
const STATE_ERROR = 'STATE_ERROR';
const STATE_HAIRCHECK = 'STATE_HAIRCHECK';

// A refresh cannot fix an expired login or a non-tutor account, so say what will.
function classroomSetupError(error, fallback) {
  const status = error?.response?.status;
  if (status === 401) return 'Your login has expired. Please log out, log back in and open the classroom again.';
  if (status === 403) return 'This account is not set up as a tutor. Please contact an admin.';
  return fallback;
}

export default function App() {
  const { loginChild } = useContext(AuthContext);
  const { user } = useAuth();
  const [appState, setAppState] = useState(STATE_IDLE);
  const [roomUrl, setRoomUrl] = useState(null);
  const [callObject, setCallObject] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [meetingToken, setMeetingToken] = useState(null);

  const [tutorEmails, setTutorEmails] = useState([]);
  const currentClassroomName = roomUrl ? roomUrl.split('/').pop() : null;

  // The tutor's owner meeting token depends on this list, and children recognise the tutor
  // by that token, so the hair check waits for it and fails visibly if it never loads.
  const tutorEmailsPromiseRef = useRef(null);
  useEffect(() => {
    const loading = loadTutorEmails().then((emails) => {
      setTutorEmails(emails);
      return emails;
    });
    loading.catch(() => {});
    tutorEmailsPromiseRef.current = loading;
  }, []);

  /**
   * Create a new call room. This function will return the newly created room URL.
   * We'll need this URL when pre-authorizing (https://docs.daily.co/reference/rn-daily-js/instance-methods/pre-auth)
   * or joining (https://docs.daily.co/reference/rn-daily-js/instance-methods/join) a call.
   */
  const createCall = useCallback((classroomName) => {
    setAppState(STATE_CREATING);
    return api
      .getRoomTutor(classroomName)
      .then((response) => {
        //loginChild(response.child);  // Login with the returned child object
        return response.url;  // Return the URL for the video call
      })
      .catch((error) => {
        console.error('Error creating room', error);
        setRoomUrl(null);
        setAppState(STATE_IDLE);
        setApiError(true);
      });
  }, []);

  /**
   * We've created a room, so let's start the hair check. We won't be joining the call yet.
   */
  const startHairCheck = useCallback(async (url) => {
    const newCallObject = DailyIframe.createCallObject({
      subscribeToTracksAutomatically: false  // Set this to false by default
    });

    // Generate meeting token for tutors to get owner privileges. Children identify the
    // tutor by this token, so a tutor must not enter the room without one.
    let emails;
    try {
      emails = await tutorEmailsPromiseRef.current;
    } catch (error) {
      console.error('Error fetching tutors after retries:', error);
      setApiError(classroomSetupError(error, 'Could not load tutor details. Please refresh the page and try again.'));
      setAppState(STATE_IDLE);
      return;
    }

    let token = null;
    if (user?.email && emails.includes(user.email)) {
      try {
        const roomName = url.split('/').pop(); // Extract room name from URL
        // The API takes the tutor from the JWT; userEmail is only read by API builds older
        // than that change, so this page works whichever side deploys first.
        const tokenResponse = await post('/classroom-allocation/meeting-token', {
          roomName,
          userEmail: user.email
        }, true);
        token = tokenResponse.token;
        setMeetingToken(token);
      } catch (error) {
        console.error('Failed to generate meeting token:', error);
        setApiError(classroomSetupError(error, 'Could not set up the classroom. Please refresh the page and try again.'));
        setAppState(STATE_IDLE);
        return;
      }
    }

    setRoomUrl(url);
    setCallObject(newCallObject);
    setAppState(STATE_HAIRCHECK);
    
    // Use token if available
    const preAuthOptions = { url };
    if (token) {
      preAuthOptions.token = token;
    }
    await newCallObject.preAuth(preAuthOptions);
    await newCallObject.startCamera();
  }, [user]);

  /**
   * Once we pass the hair check, we can actually join the call.
   * We'll pass the username entered during Haircheck to .join().
   */
  const joinCall = useCallback((userName, displayName) => {
    const joinOptions = { 
      url: roomUrl, 
      userName,  // This is still the email
      userData: {
        displayName: displayName
      }
    };

    // Include meeting token if available (for tutors)
    if (meetingToken) {
      joinOptions.token = meetingToken;
      //console.log('Joining with meeting token (tutor with owner privileges)');
    }

    callObject.join(joinOptions);
  }, [callObject, roomUrl, meetingToken]);

  /**
   * Start leaving the current call.
   */
  const startLeavingCall = useCallback(() => {
    if (!callObject) return;
    // If we're in the error state, we've already "left", so just clean up
    if (appState === STATE_ERROR) {
      callObject.destroy().then(() => {
        setRoomUrl(null);
        setCallObject(null);
        setAppState(STATE_IDLE);
      });
    } else {
      /* This will trigger a `left-meeting` event, which in turn will trigger
      the full clean-up as seen in handleNewMeetingState() below. */
      setAppState(STATE_LEAVING);
      callObject.leave();
    }
  }, [callObject, appState]);

  /**
   * If a room's already specified in the page's URL when the component mounts,
   * join the room.
   *
   * Guard with a ref so that the effect only fires once even if startHairCheck
   * recreates (it depends on the auth user). Without this guard the hair check
   * could start twice, causing a second DailyIframe.createCallObject() call while
   * the first is still mid-initialisation.
   */
  const hairCheckStartedRef = useRef(false);
  useEffect(() => {
    if (hairCheckStartedRef.current) return;
    const url = roomUrlFromPageUrl();
    if (url) {
      hairCheckStartedRef.current = true;
      startHairCheck(url);
    }
  }, [startHairCheck]);

  /**
   * Update the page's URL to reflect the active call when roomUrl changes.
   */
  useEffect(() => {
    const pageUrl = pageUrlFromRoomUrl(roomUrl);
    if (pageUrl === window.location.href) return;
    window.history.replaceState(null, null, pageUrl);
  }, [roomUrl]);

  /**
   * Update app state based on reported meeting state changes.
   *
   * NOTE: Here we're showing how to completely clean up a call with destroy().
   * This isn't strictly necessary between join()s, but is good practice when
   * you know you'll be done with the call object for a while, and you're no
   * longer listening to its events.
   */
  useEffect(() => {
    if (!callObject) return;

    const events = ['joined-meeting', 'left-meeting', 'error', 'camera-error'];

    function handleNewMeetingState() {
      switch (callObject.meetingState()) {
        case 'joined-meeting':
          setAppState(STATE_JOINED);
          break;
        case 'left-meeting':
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
          break;
        case 'error':
          setAppState(STATE_ERROR);
          break;
        default:
          break;
      }
    }

    // Use initial state
    handleNewMeetingState();

    /*
     * Listen for changes in state.
     * We can't use the useDailyEvent hook (https://docs.daily.co/reference/daily-react/use-daily-event) for this
     * because right now, we're not inside a <DailyProvider/> (https://docs.daily.co/reference/daily-react/daily-provider)
     * context yet. We can't access the call object via daily-react just yet, but we will later in Call.js and HairCheck.js!
     */
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    // Stop listening for changes in state
    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
    };
  }, [callObject]);


    // Add cleanup on unmount
    useEffect(() => {
      // Cleanup function that runs when component unmounts
      return () => {
        if (callObject) {
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
        }
      };
    }, [callObject]);
  
    // Add navigation event listener
    useEffect(() => {
      const handleBeforeUnload = (event) => {
        if (callObject) {
          callObject.leave();
          callObject.destroy();
        }
      };
  
      // Listen for navigation events
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // For React Router navigation
      window.addEventListener('popstate', handleBeforeUnload);
  
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handleBeforeUnload);
      };
    }, [callObject]);



  /**
   * Show the call UI if we're either joining, already joined, or have encountered
   * an error that is _not_ a room API error.
   */
  const showCall = !apiError && [STATE_JOINING, STATE_JOINED, STATE_ERROR].includes(appState);

  /* When there's no problems creating the room and startHairCheck() has been successfully called,
   * we can show the hair check UI. */
  const showHairCheck = !apiError && appState === STATE_HAIRCHECK;

  const renderApp = () => {
    // If something goes wrong with creating the room.
    if (apiError) {
      return (
        <div className="api-error">
          <h1>Error</h1>
          <p>
            {typeof apiError === 'string'
              ? apiError
              : 'The classroom could not be opened. Please refresh the page and try again.'}
          </p>
        </div>
      );
    }

    if (showHairCheck || showCall) {
      return (
        <DailyProvider callObject={callObject}>
          {showHairCheck ? (
            // No API errors? Let's check our hair then.
            <HairCheck joinCall={joinCall} cancelCall={startLeavingCall} />
          ) : (
            // No API errors, we passed the hair check, and we've joined the call? Then show the call.
            <>
            <div className="flex flex-col min-h-screen">
              <Call tutorEmails={tutorEmails} classroomName={currentClassroomName} lessonScheduleId={null} />
            </div>
              <Tray tutorEmails={tutorEmails} leaveCall={startLeavingCall} />
              <DailyAudio />
            </>
          )}
        </DailyProvider>
      );
    }

    // The default view is the HomeScreen, from where we start the demo.
    return <TutorUpcomingLessons createCall={createCall} startHairCheck={startHairCheck} />;
  };

  return (
    <div className="app">
      {/* <Header /> */}
      {renderApp()}
    </div>
  );
}